import JSZip from 'jszip';

const PDF_MIME_TYPE = 'application/pdf';
const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const ZIP_MIME_TYPES = ['application/zip', 'application/x-zip-compressed'];
const IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'image/webp',
  'image/svg+xml',
];

function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex >= 0 ? fileName.slice(lastDotIndex).toLowerCase() : '';
}

function getMimeTypeForExtension(extension: string): string | null {
  if (extension === '.pdf') return PDF_MIME_TYPE;
  if (extension === '.docx') return DOCX_MIME_TYPE;
  if (extension === '.png') return 'image/png';
  if (extension === '.jpg' || extension === '.jpeg') return 'image/jpeg';
  if (extension === '.gif') return 'image/gif';
  if (extension === '.bmp') return 'image/bmp';
  if (extension === '.tif' || extension === '.tiff') return 'image/tiff';
  if (extension === '.webp') return 'image/webp';
  if (extension === '.svg') return 'image/svg+xml';
  return null;
}

function isZipMetadataEntry(entryName: string): boolean {
  return entryName.startsWith('__MACOSX/') || entryName.endsWith('.DS_Store');
}

function formatZipEntryName(entryName: string): string {
  return entryName.split('/').filter(Boolean).join(' - ');
}

export function isImageFile(file: File): boolean {
  const extension = getFileExtension(file.name);
  return (
    IMAGE_MIME_TYPES.includes(file.type) ||
    ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tif', '.tiff', '.webp', '.svg'].includes(extension)
  );
}

export function isSupportedUploadFile(file: File): boolean {
  const extension = getFileExtension(file.name);

  return (
    file.type === PDF_MIME_TYPE ||
    file.type === DOCX_MIME_TYPE ||
    isImageFile(file) ||
    ZIP_MIME_TYPES.includes(file.type) ||
    extension === '.pdf' ||
    extension === '.docx' ||
    extension === '.zip'
  );
}

export function isZipUpload(file: File): boolean {
  const extension = getFileExtension(file.name);
  return ZIP_MIME_TYPES.includes(file.type) || extension === '.zip';
}

export async function expandZipToSupportedDocuments(file: File): Promise<File[]> {
  const zip = await JSZip.loadAsync(file);
  const extractedFiles: File[] = [];
  const unsupportedEntries: string[] = [];

  await Promise.all(
    Object.values(zip.files).map(async (entry) => {
      if (entry.dir || isZipMetadataEntry(entry.name)) {
        return;
      }

      const normalizedName = entry.name.replace(/\\/g, '/');
      const formattedName = formatZipEntryName(normalizedName);
      const mimeType = getMimeTypeForExtension(getFileExtension(formattedName));

      if (!mimeType) {
        unsupportedEntries.push(normalizedName);
        return;
      }

      const blob = await entry.async('blob');
      extractedFiles.push(
        new File([blob], formattedName, {
          type: mimeType,
          lastModified: file.lastModified,
        })
      );
    })
  );

  if (unsupportedEntries.length > 0) {
    const sample = unsupportedEntries.slice(0, 3).join(', ');
    const extraCount = unsupportedEntries.length - 3;
    throw new Error(
      `ZIP contains unsupported files: ${sample}${extraCount > 0 ? ` and ${extraCount} more` : ''}`
    );
  }

  if (extractedFiles.length === 0) {
    throw new Error('ZIP does not contain any supported PDF, DOCX, or image files');
  }

  return extractedFiles;
}
